import { useState, useCallback } from "react";
import { useQuery } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import { format } from "date-fns";
import { useDateLocale } from "@/hooks/useDateLocale";
import {
  Calendar,
  ChevronDown,
  Download,
  Banknote,
  DollarSign,
  Shield,
  ArrowUpRight,
  ArrowDownRight,
  Sparkles,
  FileText,
  Gift,
  PhoneCall,
  AlertTriangle,
  Share2,
} from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { apiGetMySalary, type MySalaryPayslip } from "@/lib/api";
import { formatMKD, RATES } from "@/lib/mk-salary";
import { canShareFiles, shareFile } from "@/lib/share";
import { haptic } from "@/lib/haptics";
import { PDFDocument, StandardFonts, rgb, PageSizes } from "pdf-lib";
import MobileLayout from "./MobileLayout";

const MONTH_SHORT = ["Jan","Feb","Mar","Apr","May","Jun","Jul","Aug","Sep","Oct","Nov","Dec"];
const MONTH_FULL  = ["January","February","March","April","May","June","July","August","September","October","November","December"];

const adjTypeConfig: Record<string, { label: string }> = {
  bonus:             { label: "Bonus" },
  deduction:         { label: "Deduction" },
  on_call:           { label: "On-Call" },
  admin_prohibition: { label: "Adm. Zabrana" },
  other:             { label: "Other" },
};

// ── PDF helper (same logic as desktop MySalary) ───────────────────────────────
async function buildPayslipPDF(slip: MySalaryPayslip): Promise<{ blob: Blob; filename: string }> {
  const pdfDoc  = await PDFDocument.create();
  const page    = pdfDoc.addPage(PageSizes.A4);
  const { width, height } = page.getSize();

  const bold    = await pdfDoc.embedFont(StandardFonts.HelveticaBold);
  const regular = await pdfDoc.embedFont(StandardFonts.Helvetica);

  const adjustedGross = slip.grossSalary + slip.totalAdjustmentsGross;
  const monthName     = MONTH_FULL[slip.month - 1];
  const primary = rgb(0.22, 0.34, 0.78);
  const light   = rgb(0.95, 0.96, 1.0);
  const dark    = rgb(0.1, 0.1, 0.1);
  const muted   = rgb(0.5, 0.5, 0.5);
  const divider = rgb(0.85, 0.85, 0.88);
  const marginX = 50;
  const colRight = width - marginX;

  page.drawRectangle({ x: 0, y: height - 80, width, height: 80, color: primary });
  page.drawText("BestHR", { x: marginX, y: height - 38, size: 22, font: bold, color: rgb(1,1,1) });
  page.drawText("PAY SLIP", { x: marginX, y: height - 60, size: 11, font: regular, color: rgb(0.8,0.85,1) });

  const period  = `${monthName} ${slip.year}`;
  const periodW = bold.widthOfTextAtSize(period, 16);
  page.drawText(period, { x: colRight - periodW, y: height - 44, size: 16, font: bold, color: rgb(1,1,1) });
  page.drawText(slip.runStatus, {
    x: colRight - regular.widthOfTextAtSize(slip.runStatus, 10),
    y: height - 62, size: 10, font: regular, color: rgb(0.8,0.85,1),
  });

  let rowY = height - 112;

  function drawSection(title: string) {
    page.drawRectangle({ x: marginX, y: rowY - 4, width: colRight - marginX, height: 20, color: light });
    page.drawText(title.toUpperCase(), { x: marginX + 6, y: rowY, size: 8, font: bold, color: primary });
    rowY -= 26;
  }

  function drawRow(label: string, amount: number, opts?: { bold?: boolean; primary?: boolean; positive?: boolean; negative?: boolean }) {
    const isBold  = opts?.bold;
    const isStr   = opts?.primary;
    const font    = (isBold || isStr) ? bold : regular;
    const size    = isStr ? 11 : 9;

    if (isStr) page.drawLine({ start: { x: marginX, y: rowY + 10 }, end: { x: colRight, y: rowY + 10 }, thickness: 0.5, color: divider });
    page.drawText(label, { x: marginX + 6, y: rowY, size, font, color: isStr ? primary : dark });

    const fmtAmt  = formatMKD(Math.abs(amount)) + " MKD";
    const prefix  = opts?.positive ? "+" : (opts?.negative || amount < 0) ? "-" : "";
    const amtText = prefix + fmtAmt;
    const amtColor = opts?.positive ? rgb(0,0.55,0.35) : (opts?.negative || amount < 0) ? rgb(0.75,0.1,0.1) : isStr ? primary : dark;
    const amtW = font.widthOfTextAtSize(amtText, size);
    page.drawText(amtText, { x: colRight - amtW, y: rowY, size, font, color: amtColor });

    if (isStr) { rowY -= 16; }
    else { page.drawLine({ start: { x: marginX, y: rowY - 5 }, end: { x: colRight, y: rowY - 5 }, thickness: 0.3, color: divider }); rowY -= 18; }
  }

  drawSection("Gross Earnings");
  drawRow("Base Gross Salary", slip.grossSalary);
  if (slip.totalAdjustmentsGross !== 0) {
    drawRow("Gross Adjustments", slip.totalAdjustmentsGross, { positive: slip.totalAdjustmentsGross > 0, negative: slip.totalAdjustmentsGross < 0 });
    drawRow("Adjusted Gross", adjustedGross, { bold: true });
  }
  rowY -= 8;
  drawSection("Deductions");
  drawRow(`Pension (${(RATES.pension * 100).toFixed(1)}%)`, slip.pensionContribution);
  drawRow(`Health Insurance (${(RATES.health * 100).toFixed(1)}%)`, slip.healthContribution);
  drawRow(`Unemployment (${(RATES.unemployment * 100).toFixed(1)}%)`, slip.unemploymentContribution);
  drawRow(`Sickness Insurance (${(RATES.sickness * 100).toFixed(1)}%)`, slip.sicknessContribution);
  drawRow("Total Contributions", slip.totalContributions, { bold: true });
  drawRow("Gross - Contributions", slip.grossMinusContributions);
  drawRow("Personal Exemption", slip.personalExemption);
  drawRow("Tax Base", slip.taxBase);
  drawRow("Income Tax (10%)", slip.personalIncomeTax);
  drawRow("Total Deductions", slip.totalDeductions, { bold: true });
  drawRow("Calculated Net", slip.netSalary, { bold: true });

  const netAdj = slip.adjustments.filter((a) => a.applyOn === "net");
  if (netAdj.length > 0) {
    rowY -= 8;
    drawSection("Net Adjustments");
    for (const a of netAdj) {
      const sign = (a.type === "bonus" || a.type === "on_call") ? 1 : -1;
      drawRow(a.name, sign * a.amount, { positive: sign > 0, negative: sign < 0 });
    }
  }

  rowY -= 12;
  page.drawRectangle({ x: marginX, y: rowY - 8, width: colRight - marginX, height: 30, color: primary });
  rowY += 6;
  page.drawText("FINAL NET SALARY", { x: marginX + 8, y: rowY, size: 11, font: bold, color: rgb(1,1,1) });
  const finalText = formatMKD(slip.finalNetSalary) + " MKD";
  const finalW = bold.widthOfTextAtSize(finalText, 13);
  page.drawText(finalText, { x: colRight - finalW - 8, y: rowY, size: 13, font: bold, color: rgb(1,1,1) });

  page.drawText("Generated by BestHR  •  Confidential", { x: marginX, y: 24, size: 8, font: regular, color: muted });
  const dateStr = new Date().toLocaleDateString("en-GB");
  page.drawText(dateStr, { x: colRight - regular.widthOfTextAtSize(dateStr, 8), y: 24, size: 8, font: regular, color: muted });

  const bytes = await pdfDoc.save();
  const blob  = new Blob([bytes as BlobPart], { type: "application/pdf" });
  const filename = `payslip-${slip.year}-${String(slip.month).padStart(2, "0")}.pdf`;
  return { blob, filename };
}

async function downloadPayslipPDF(slip: MySalaryPayslip) {
  const { blob, filename } = await buildPayslipPDF(slip);
  const url = URL.createObjectURL(blob);
  const a   = document.createElement("a");
  a.href     = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
}

// ── Breakdown row for expanded payslip ───────────────────────────────────────
function BRow({ label, value, bold, primary, positive, negative }: {
  label: string; value: number; bold?: boolean; primary?: boolean; positive?: boolean; negative?: boolean;
}) {
  const rowCls = primary ? "bg-primary/5 font-bold" : bold ? "font-semibold" : "";
  const valCls = primary
    ? "text-primary font-bold"
    : positive ? "text-emerald-600 font-medium"
    : negative ? "text-red-600 font-medium"
    : bold ? "font-semibold" : "";
  return (
    <div className={`flex items-center justify-between px-3 py-2 border-b border-border/30 last:border-0 ${rowCls}`}>
      <span className="text-xs text-foreground/80">{label}</span>
      <span className={`text-xs tabular-nums ${valCls}`}>
        {positive && value > 0 ? "+" : ""}{formatMKD(value)} MKD
      </span>
    </div>
  );
}

// ── Single payslip card ───────────────────────────────────────────────────────
function PayslipCard({ slip, index }: { slip: MySalaryPayslip; index: number }) {
  const { t } = useTranslation();
  const dateLocale = useDateLocale();
  const [expanded, setExpanded] = useState(false);
  const [downloading, setDownloading] = useState(false);
  const [sharing, setSharing] = useState(false);
  const adjustedGross = slip.grossSalary + slip.totalAdjustmentsGross;

  const handleDownload = useCallback(async (e: React.MouseEvent) => {
    e.stopPropagation();
    setDownloading(true);
    try { await downloadPayslipPDF(slip); }
    catch (err) { console.error("PDF error:", err); }
    finally { setDownloading(false); }
  }, [slip]);

  const handleShare = useCallback(async (e: React.MouseEvent) => {
    e.stopPropagation();
    haptic();
    setSharing(true);
    try {
      const { blob, filename } = await buildPayslipPDF(slip);
      const file = new File([blob], filename, { type: "application/pdf" });
      const shared = await shareFile(file, { title: filename });
      if (!shared) await downloadPayslipPDF(slip); // fall back to download
    } catch (err) {
      console.error("Share error:", err);
    } finally {
      setSharing(false);
    }
  }, [slip]);

  const showShare = canShareFiles();

  const netAdj = slip.adjustments.filter((a) => a.applyOn === "net");

  return (
    <div
      className="bg-card border border-border/60 rounded-2xl overflow-hidden animate-in fade-in slide-in-from-bottom-3 duration-400"
      style={{ animationDelay: `${Math.min(index * 40, 240)}ms` }}
    >
      {/* Top row: month + net amount */}
      <div className="flex items-center justify-between px-4 pt-4 pb-2">
        <div>
          <p className="text-sm font-semibold text-foreground">
            {format(new Date(slip.year, slip.month - 1, 1), "MMMM yyyy", { locale: dateLocale })}
          </p>
          <div className="flex items-center gap-1.5 mt-1">
            <Badge variant="secondary" className="text-[10px] font-normal capitalize px-1.5 py-0">{slip.runStatus}</Badge>
            {slip.adjustments.length > 0 && (
              <Badge variant="outline" className="text-[10px] font-normal px-1.5 py-0">{slip.adjustments.length} {t("mobile.salary.adj")}</Badge>
            )}
          </div>
        </div>
        <div className="text-right">
          <p className="text-xl font-bold text-foreground tabular-nums">{formatMKD(slip.finalNetSalary)}</p>
          <p className="text-[10px] text-muted-foreground">{t("mobile.salary.netPay")} · MKD</p>
        </div>
      </div>

      {/* Bottom row: gross label + download + expand */}
      <div className="flex items-center justify-between px-4 pb-3">
        <p className="text-xs text-muted-foreground">
          {t("mobile.salary.grossLabel")}: <span className="font-medium text-foreground">{formatMKD(adjustedGross)} MKD</span>
        </p>
        <div className="flex items-center gap-1">
          {showShare && (
            <Button
              variant="ghost"
              size="sm"
              className="h-7 px-2 text-xs gap-1 text-muted-foreground"
              disabled={sharing}
              onClick={handleShare}
            >
              <Share2 className={`h-3.5 w-3.5 ${sharing ? "animate-pulse" : ""}`} />
              {t("mobile.salary.share", "Share")}
            </Button>
          )}
          <Button
            variant="ghost"
            size="sm"
            className="h-7 px-2 text-xs gap-1 text-muted-foreground"
            disabled={downloading}
            onClick={handleDownload}
          >
            <Download className={`h-3.5 w-3.5 ${downloading ? "animate-pulse" : ""}`} />
            PDF
          </Button>
          <button
            type="button"
            onClick={() => setExpanded((v) => !v)}
            className="h-7 px-2 flex items-center gap-1 text-xs text-muted-foreground rounded-md hover:bg-muted/40 active:bg-muted/60 transition-colors"
          >
            <span>{expanded ? t("mobile.salary.hide") : t("mobile.salary.details")}</span>
            <ChevronDown className={`h-3.5 w-3.5 transition-transform duration-200 ${expanded ? "rotate-180" : ""}`} />
          </button>
        </div>
      </div>

      {/* Expandable breakdown */}
      <div className={`overflow-hidden transition-all duration-300 ${expanded ? "max-h-[1200px]" : "max-h-0"}`}>
        <div className="border-t border-border/40 mx-3 mb-3 mt-0 rounded-xl overflow-hidden bg-muted/20">
          {/* Gross */}
          <div className="px-3 py-1.5 bg-muted/40 border-b border-border/30">
            <span className="text-[10px] font-semibold uppercase text-muted-foreground tracking-wider">{t("mobile.salary.grossEarnings")}</span>
          </div>
          <BRow label={t("mobile.salary.baseGross")} value={slip.grossSalary} bold />
          {slip.totalAdjustmentsGross !== 0 && (
            <>
              <BRow label={t("mobile.salary.grossAdjustments")} value={slip.totalAdjustmentsGross}
                positive={slip.totalAdjustmentsGross > 0} negative={slip.totalAdjustmentsGross < 0} />
              <BRow label={t("mobile.salary.adjustedGross")} value={adjustedGross} bold />
            </>
          )}
          {/* Deductions */}
          <div className="px-3 py-1.5 bg-muted/40 border-y border-border/30 mt-1">
            <span className="text-[10px] font-semibold uppercase text-muted-foreground tracking-wider">{t("mobile.salary.deductions")}</span>
          </div>
          <BRow label={`${t("mobile.salary.pension")} (${(RATES.pension * 100).toFixed(1)}%)`} value={slip.pensionContribution} />
          <BRow label={`${t("mobile.salary.health")} (${(RATES.health * 100).toFixed(1)}%)`} value={slip.healthContribution} />
          <BRow label={`${t("mobile.salary.unemployment")} (${(RATES.unemployment * 100).toFixed(1)}%)`} value={slip.unemploymentContribution} />
          <BRow label={`${t("mobile.salary.sickness")} (${(RATES.sickness * 100).toFixed(1)}%)`} value={slip.sicknessContribution} />
          <BRow label={t("mobile.salary.totalContributions")} value={slip.totalContributions} bold />
          <BRow label={t("mobile.salary.grossMinusContrib")} value={slip.grossMinusContributions} />
          <BRow label={t("mobile.salary.personalExemption")} value={slip.personalExemption} />
          <BRow label={t("mobile.salary.taxBase")} value={slip.taxBase} />
          <BRow label={`${t("mobile.salary.incomeTax")} (10%)`} value={slip.personalIncomeTax} />
          <BRow label={t("mobile.salary.totalDeductions")} value={slip.totalDeductions} bold />
          <BRow label={t("mobile.salary.calculatedNet")} value={slip.netSalary} bold />
          {/* Net adjustments */}
          {netAdj.length > 0 && (
            <>
              <div className="px-3 py-1.5 bg-muted/40 border-y border-border/30 mt-1">
                <span className="text-[10px] font-semibold uppercase text-muted-foreground tracking-wider">{t("mobile.salary.netAdjustments")}</span>
              </div>
              {netAdj.map((a) => {
                const sign = (a.type === "bonus" || a.type === "on_call") ? 1 : -1;
                const cfg  = adjTypeConfig[a.type] ?? adjTypeConfig.other;
                return (
                  <div key={a.id} className="flex items-center justify-between px-3 py-2 border-b border-border/30 last:border-0">
                    <span className="text-xs text-foreground/80">
                      {a.name}
                      <span className="ml-1.5 text-[10px] text-muted-foreground">({cfg.label})</span>
                    </span>
                    <span className={`text-xs tabular-nums font-medium ${sign > 0 ? "text-emerald-600" : "text-red-600"}`}>
                      {sign > 0 ? "+" : "-"}{formatMKD(a.amount)} MKD
                    </span>
                  </div>
                );
              })}
            </>
          )}
          {/* Final net highlight */}
          <div className="flex items-center justify-between px-3 py-3 bg-primary/10 mt-1">
            <span className="text-xs font-bold text-primary">{t("mobile.salary.finalNetSalary")}</span>
            <span className="text-sm font-bold text-primary tabular-nums">{formatMKD(slip.finalNetSalary)} MKD</span>
          </div>
        </div>
      </div>
    </div>
  );
}

// ── Main page ─────────────────────────────────────────────────────────────────
export default function MobileSalary() {
  const { t } = useTranslation();
  const dateLocale = useDateLocale();
  const { data, isLoading } = useQuery({
    queryKey: ["mobile-my-salary"],
    queryFn: apiGetMySalary,
    staleTime: 5 * 60_000,
  });

  const latest   = data?.payslips[0] ?? null;
  const previous = data?.payslips[1] ?? null;
  const netDiff  = latest && previous ? latest.finalNetSalary - previous.finalNetSalary : 0;
  const netPct   = previous && previous.finalNetSalary > 0
    ? ((netDiff / previous.finalNetSalary) * 100).toFixed(1)
    : null;

  return (
    <MobileLayout title={t("mobile.salary.title")}>
      <div className="px-4 py-4 space-y-4 pb-6">

        {/* ── Summary strip ──────────────────────────────────────────── */}
        {isLoading && (
          <div className="space-y-3 animate-pulse">
            <div className="h-28 bg-muted rounded-2xl" />
            <div className="grid grid-cols-2 gap-3">
              <div className="h-16 bg-muted rounded-xl" />
              <div className="h-16 bg-muted rounded-xl" />
            </div>
            <div className="h-20 bg-muted rounded-2xl" />
            <div className="h-20 bg-muted rounded-2xl" />
          </div>
        )}

        {!isLoading && latest && (
          <>
            {/* Hero: latest net pay */}
            <div className="bg-card border border-border/60 rounded-2xl px-5 py-5 text-center">
              <p className="text-xs text-muted-foreground mb-1">
                {t("mobile.salary.latestNetPay")} — {format(new Date(latest.year, latest.month - 1, 1), "MMMM yyyy", { locale: dateLocale })}
              </p>
              <p className="text-4xl font-bold tabular-nums text-foreground tracking-tight">
                {formatMKD(latest.finalNetSalary)}
                <span className="text-base font-normal text-muted-foreground ml-1">MKD</span>
              </p>
              {netPct && (
                <div className={`inline-flex items-center gap-1 mt-2 text-xs font-medium ${netDiff >= 0 ? "text-emerald-600" : "text-red-500"}`}>
                  {netDiff >= 0 ? <ArrowUpRight className="h-3.5 w-3.5" /> : <ArrowDownRight className="h-3.5 w-3.5" />}
                  {netDiff >= 0 ? "+" : ""}{netPct}% {t("mobile.salary.vsPreviousMonth")}
                </div>
              )}
            </div>

            {/* Two compact stat pills */}
            <div className="grid grid-cols-2 gap-3">
              <div className="bg-card border border-border/60 rounded-xl px-4 py-3 flex items-center gap-3">
                <div className="h-8 w-8 rounded-lg bg-blue-500/10 flex items-center justify-center shrink-0">
                  <DollarSign className="h-4 w-4 text-blue-600" />
                </div>
                <div>
                  <p className="text-[10px] text-muted-foreground">{t("mobile.salary.gross")}</p>
                  <p className="text-sm font-bold tabular-nums text-foreground">
                    {formatMKD(latest.grossSalary + latest.totalAdjustmentsGross)}
                    <span className="text-[10px] font-normal text-muted-foreground ml-0.5">MKD</span>
                  </p>
                </div>
              </div>
              <div className="bg-card border border-border/60 rounded-xl px-4 py-3 flex items-center gap-3">
                <div className="h-8 w-8 rounded-lg bg-amber-500/10 flex items-center justify-center shrink-0">
                  <Shield className="h-4 w-4 text-amber-600" />
                </div>
                <div>
                  <p className="text-[10px] text-muted-foreground">{t("mobile.salary.deductions")}</p>
                  <p className="text-sm font-bold tabular-nums text-foreground">
                    {formatMKD(latest.totalDeductions)}
                    <span className="text-[10px] font-normal text-muted-foreground ml-0.5">MKD</span>
                  </p>
                </div>
              </div>
            </div>
          </>
        )}

        {/* ── No data ─────────────────────────────────────────────────── */}
        {!isLoading && !latest && (
          <div className="flex flex-col items-center gap-3 py-16 text-muted-foreground">
            <Sparkles className="h-12 w-12 opacity-30" />
            <p className="text-sm font-medium text-foreground">{t("mobile.salary.noPayslips")}</p>
          </div>
        )}

        {/* ── Payslip list ─────────────────────────────────────────────── */}
        {!isLoading && (data?.payslips ?? []).length > 0 && (
          <div className="space-y-2">
            <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wider px-1">
              {t("mobile.salary.allPayslips")} ({data!.payslips.length})
            </p>
            {data!.payslips.map((slip, i) => (
              <PayslipCard key={slip.id} slip={slip} index={i} />
            ))}
          </div>
        )}
      </div>
    </MobileLayout>
  );
}
